import React from 'react';
import { useMode } from '../context/ModeContext';
import { useLiveQuery } from "dexie-react-hooks";
import { db } from "../db";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Building2, User, Globe } from "lucide-react";
import { useUserRole } from "../hooks/useUserRole";

export function ModeSelector() {
    const { activeMode, setActiveMode } = useMode();
    const doctorId = localStorage.getItem('user_id');
    const { isReceptionistOnly } = useUserRole();

    // Fetch clinics only (the hook handles the role check)
    const clinics = useLiveQuery(async () => {
        if (!doctorId) return [];
        const memberships = await db.clinic_members
            .where('user_id').equals(doctorId)
            .filter(m => m.is_active !== false)
            .toArray();
        const clinicIds = memberships.map(m => m.clinic_id);
        return db.clinics.where('id').anyOf(clinicIds).toArray();
    }, [doctorId]) || [];

    // Force switch if in restricted mode and trying to access forbidden modes
    React.useEffect(() => {
        if (isReceptionistOnly && (activeMode === 'personal' || activeMode === 'everything') && clinics.length > 0) {
            setActiveMode(clinics[0].id);
        }
    }, [isReceptionistOnly, activeMode, clinics, setActiveMode]);

    return (
        <div className="w-full max-w-[240px]">
            <Select value={activeMode} onValueChange={setActiveMode}>
                <SelectTrigger className="w-full bg-white text-slate-900 border-slate-200 hover:bg-slate-50 transition-colors shadow-sm">
                    <SelectValue placeholder="Select View Mode">
                        <div className="flex items-center gap-2">
                            {activeMode === 'personal' && <User size={16} className="text-blue-500" />}
                            {activeMode === 'everything' && <Globe size={16} className="text-purple-500" />}
                            {activeMode !== 'personal' && activeMode !== 'everything' && <Building2 size={16} className="text-green-600" />}
                            <span className="truncate">
                                {activeMode === 'personal' ? 'Personal Mode' :
                                    activeMode === 'everything' ? 'Everything Mode' :
                                        clinics.find(c => c.id === activeMode)?.name || 'Unknown Clinic'}
                            </span>
                        </div>
                    </SelectValue>
                </SelectTrigger>
                <SelectContent>
                    {!isReceptionistOnly && (
                        <>
                            <SelectItem value="personal">
                                <div className="flex items-center gap-2">
                                    <User size={16} className="text-blue-500" />
                                    <div className="flex flex-col text-left">
                                        <span className="font-medium">Personal Mode</span>
                                        <span className="text-xs text-muted-foreground">My private patients & appointments</span>
                                    </div>
                                </div>
                            </SelectItem>
                            <SelectItem value="everything">
                                <div className="flex items-center gap-2">
                                    <Globe size={16} className="text-purple-500" />
                                    <div className="flex flex-col text-left">
                                        <span className="font-medium">Everything Mode</span>
                                        <span className="text-xs text-muted-foreground">All clinics and personal data</span>
                                    </div>
                                </div>
                            </SelectItem>
                        </>
                    )}

                    {clinics.length > 0 && (
                        <>
                            <div className="px-2 py-1.5 text-xs font-semibold text-muted-foreground mt-2">Clinics</div>
                            {clinics.map(clinic => (
                                <SelectItem key={clinic.id} value={clinic.id}>
                                    <div className="flex items-center gap-2">
                                        <Building2 size={16} className="text-green-600" />
                                        <span>{clinic.name}</span>
                                    </div>
                                </SelectItem>
                            ))}
                        </>
                    )}
                </SelectContent>
            </Select>
        </div>
    );
}
